import { snoozeDeadline, STALE_BUILD_IGNORE_SNOOZE_MS, type RecoveryRecord } from './updatePolicy';

const PENDING_KEY = 'autofluxo_update_pending_version';
const APPLIED_KEY = 'autofluxo_update_applied_version';
const ATTEMPT_KEY = 'autofluxo_update_attempt';
const BUILD_KEY = 'autofluxo_loaded_build';
const DB_PENDING_KEY = 'autofluxo_update_pending_db_version';
const REMOTE_BUILD_KEY = 'autofluxo_remote_build';
const LAST_CHECK_KEY = 'autofluxo_update_last_check';
export const SNOOZE_KEY = 'autofluxo_update_snoozed_until';
const DB_KEY = 'app_build_version';
let loadedAttemptAt: number | null = null;

function read(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
function write(key: string, value: string) { try { localStorage.setItem(key, value); } catch { /* ignore */ } }
function remove(key: string) { try { localStorage.removeItem(key); } catch { /* ignore */ } }

export function setPendingUpdate(version: string) {
  // O SW não substitui uma versão concreta, nem um aviso obrigatório do banco.
  if (!version.startsWith('sw:') || !read(PENDING_KEY)) write(PENDING_KEY, version);
}
export function getPendingUpdate(): string | null { return read(PENDING_KEY) || read(DB_PENDING_KEY); }
export function clearPendingUpdate() { remove(PENDING_KEY); }
export function isMandatoryUpdate() { return !!read(DB_PENDING_KEY); }
export function markUpdateAttempt(version: string) {
  write(ATTEMPT_KEY, JSON.stringify({ version, at: Date.now(), build: getCurrentBuild(), db: read(DB_PENDING_KEY) }));
}
export function getUpdateAttempt(): { version: string; at: number; build: string | null; db?: string | null } | null {
  try { return JSON.parse(read(ATTEMPT_KEY) || 'null'); } catch { return null; }
}
export function clearUpdateAttempt() { remove(ATTEMPT_KEY); }
export function markUpdateApplied(version: string) {
  const db = read(DB_PENDING_KEY) || (!/^(build:|sw:)/.test(version) ? version : null);
  if (db) { write(DB_KEY, db); write(APPLIED_KEY, db); }
  else write(APPLIED_KEY, version);
  remove(PENDING_KEY); remove(DB_PENDING_KEY); remove(ATTEMPT_KEY); remove(SNOOZE_KEY);
}
export function getAppliedVersion() { return read(APPLIED_KEY); }
export function getCurrentBuild(): string { return String(import.meta.env.VITE_BUILD_VERSION ?? 'desconhecido'); }
export function getLoadedBuild() { return read(BUILD_KEY); }
export function getRemoteBuild() { return read(REMOTE_BUILD_KEY); }
export function getLastUpdateCheck() { return Number(read(LAST_CHECK_KEY) || 0); }
export function recordUpdateCheck(build?: string) {
  write(LAST_CHECK_KEY, String(Date.now()));
  if (build) write(REMOTE_BUILD_KEY, build);
  window.dispatchEvent(new Event('autofluxo:update-status'));
}
export function getSnoozedUntil() {
  const until = Number(read(SNOOZE_KEY) || 0);
  // Build atrasado há mais de 24 h: o adiamento comum deixa de valer.
  const rec = getRecovery();
  if (rec && rec.target !== getCurrentBuild() && Date.now() - rec.firstAt >= STALE_BUILD_IGNORE_SNOOZE_MS) return 0;
  // Um bump obrigatório nunca herda uma hora inteira de adiamento.
  return isMandatoryUpdate() ? Math.min(until, Number(read('autofluxo_mandatory_detected_at') || Date.now()) + 15 * 60 * 1000) : until;
}
export function snoozeUpdate() { write(SNOOZE_KEY, String(snoozeDeadline(isMandatoryUpdate(), Date.now()))); }

/** Concilia o build confirmado pela rede e o sinal opcional do SuperAdmin. */
export function reconcileUpdateVersions(remoteBuild: string | null, remoteDb: string | null) {
  const current = getCurrentBuild();
  const attempt = getUpdateAttempt();
  if (remoteBuild && remoteBuild !== current) setPendingUpdate(`build:${remoteBuild}`);
  if (remoteBuild === current) {
    if (read(PENDING_KEY)?.startsWith('build:')) remove(PENDING_KEY);
    // Um reload manual obrigatório pode carregar o mesmo build: a consulta
    // sem cache prova que não há um deploy faltando, encerrando a tentativa.
    if (attempt && loadedAttemptAt === attempt.at && ((attempt.db && remoteDb === attempt.db) || attempt.version.startsWith('sw:'))) markUpdateApplied(attempt.version);
  }
  if (remoteDb) {
    const local = read(DB_KEY);
    if (read(APPLIED_KEY) === remoteDb) {
      write(DB_KEY, remoteDb);
      if (read(DB_PENDING_KEY) === remoteDb) remove(DB_PENDING_KEY);
      if (read(PENDING_KEY) === remoteDb) remove(PENDING_KEY);
    } else if (!local) write(DB_KEY, remoteDb);
    else if (local !== remoteDb) {
      if (read(DB_PENDING_KEY) !== remoteDb) write('autofluxo_mandatory_detected_at', String(Date.now()));
      write(DB_PENDING_KEY, remoteDb);
      if (remoteBuild === current) write(DB_KEY, remoteDb);
    }
  }
  window.dispatchEvent(new Event('autofluxo:update-status'));
  if (getPendingUpdate()) window.dispatchEvent(new Event('autofluxo:update-available'));
}

export function confirmUpdateAfterLoad(): { confirmed: boolean; stillPending: string | null } {
  const build = getCurrentBuild();
  const attempt = getUpdateAttempt();
  loadedAttemptAt = attempt?.at ?? null;
  write(BUILD_KEY, build);
  if (!attempt) return { confirmed: false, stillPending: getPendingUpdate() };
  const exactBuild = attempt.version.startsWith('build:');
  const applied = exactBuild ? attempt.version === `build:${build}` : !!attempt.build && attempt.build !== build;
  if (applied) { markUpdateApplied(attempt.version); return { confirmed: true, stillPending: null }; }
  return { confirmed: false, stillPending: getPendingUpdate() || attempt.version };
}

// ---- Rede de segurança e diagnóstico ----
const RECOVERY_KEY = 'autofluxo_update_recovery';
const SW_ERROR_KEY = 'autofluxo_sw_last_error';
let countedThisLoad = false;

export function getRecovery(): RecoveryRecord | null {
  try { return JSON.parse(read(RECOVERY_KEY) || 'null'); } catch { return null; }
}
export function saveRecovery(rec: RecoveryRecord | null) {
  if (rec) write(RECOVERY_KEY, JSON.stringify(rec)); else remove(RECOVERY_KEY);
}
/** Conta uma tentativa por carregamento enquanto o build publicado for outro. */
export function trackStaleBuild(remoteBuild: string | null): RecoveryRecord | null {
  if (!remoteBuild) return getRecovery();
  if (remoteBuild === getCurrentBuild()) { saveRecovery(null); return null; }
  let rec = getRecovery();
  if (!rec || rec.target !== remoteBuild) { rec = { target: remoteBuild, attempts: 0, firstAt: Date.now() }; countedThisLoad = true; }
  else if (!countedThisLoad) { rec = { ...rec, attempts: rec.attempts + 1 }; countedThisLoad = true; }
  saveRecovery(rec);
  return rec;
}
export type SwErrorRecord = { reason: string; target: string | null; at: number };
export function recordSwError(reason: string, target?: string | null) {
  write(SW_ERROR_KEY, JSON.stringify({ reason, target: target ?? getRemoteBuild(), at: Date.now() }));
  window.dispatchEvent(new Event('autofluxo:update-status'));
}
export function getSwError(): SwErrorRecord | null {
  try { return JSON.parse(read(SW_ERROR_KEY) || 'null'); } catch { return null; }
}
